import { useState, useEffect } from 'react';
import { Wifi, WifiOff, MapPin } from 'lucide-react';

// Hook pour mesurer la vitesse WiFi
export const useNetworkInfo = () => {
  const [networkInfo, setNetworkInfo] = useState({
    ssid: 'Unknown',
    speed: 0,
    type: 'unknown',
    effectiveType: '4g',
    downlink: 10,
    rtt: 50,
  });

  useEffect(() => {
    const updateNetworkInfo = () => {
      // Network Information API
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (connection) {
        setNetworkInfo({
          ssid: 'WiFi Network', // Note: SSID not available in browser for security
          speed: connection.downlink || 0, // Mbps
          type: connection.type || 'wifi',
          effectiveType: connection.effectiveType || '4g',
          downlink: connection.downlink || 10,
          rtt: connection.rtt || 50, // Round-trip time
        });
      }
    };

    updateNetworkInfo();

    // Update on connection change
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.addEventListener('change', updateNetworkInfo);
      return () => connection.removeEventListener('change', updateNetworkInfo);
    }
  }, []);

  return networkInfo;
};

// Network Status Component
export const NetworkStatus = ({ compact = false }) => {
  const network = useNetworkInfo();
  
  const getSpeedColor = (speed) => {
    if (speed >= 10) return '#00ff9f';
    if (speed >= 5) return '#ffff00';
    return '#ff0000';
  };

  const getSignalBars = (speed) => {
    if (speed >= 10) return 4;
    if (speed >= 5) return 3;
    if (speed >= 2) return 2;
    return 1;
  };

  if (compact) {
    return (
      <div className="flex items-center space-x-2 text-xs font-mono">
        <Wifi 
          className="w-4 h-4" 
          style={{ color: getSpeedColor(network.downlink) }}
        />
        <span style={{ color: getSpeedColor(network.downlink) }}>
          {network.downlink.toFixed(1)} Mbps
        </span>
      </div>
    );
  }

  return (
    <div className="p-3 bg-black bg-opacity-50 border border-cyan-400 rounded">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Wifi className="w-4 h-4 text-cyan-400" />
          <span className="text-xs text-cyan-400 font-mono uppercase">RÉSEAU</span>
        </div>
        <div className="flex space-x-1">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="w-1 rounded-full"
              style={{
                height: `${(i + 1) * 3}px`,
                backgroundColor: i < getSignalBars(network.downlink) ? getSpeedColor(network.downlink) : '#333',
              }}
            />
          ))}
        </div>
      </div>
      <div className="text-xs font-mono">
        <div style={{ color: getSpeedColor(network.downlink) }}>
          ⚡ {network.downlink.toFixed(1)} Mbps
        </div>
        <div className="text-gray-500">
          Latency: {network.rtt}ms
        </div>
      </div>
    </div>
  );
};

// Hook pour gérer la pièce
export const useRoomSelector = () => {
  const [currentRoom, setCurrentRoom] = useState(() => {
    const saved = localStorage.getItem('currentRoom');
    return saved || null; // null = pas encore sélectionnée
  });

  const [availableRooms, setAvailableRooms] = useState([
    'Salon',
    'Cuisine',
    'Chambre parentale',
    'Chambre enfant',
    'Salle de bain',
    'Bureau',
    'Garage',
    'Entrée',
    'Jardin',
  ]);

  const selectRoom = (room) => {
    setCurrentRoom(room);
    localStorage.setItem('currentRoom', room);
  };

  return { currentRoom, availableRooms, selectRoom };
};

// Room Selector Component
export const RoomSelector = ({ compact = false }) => {
  const { currentRoom, availableRooms, selectRoom } = useRoomSelector();
  const [showDropdown, setShowDropdown] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`w-full flex items-center justify-between px-2 py-1.5 bg-black/50 border rounded text-[10px] font-mono hover:bg-black/70 transition-all ${
          currentRoom ? 'border-cyan-400/50' : 'border-yellow-500/50 animate-pulse'
        }`}
      >
        <span className="flex items-center space-x-1 truncate">
          <MapPin className={`w-3 h-3 flex-shrink-0 ${currentRoom ? 'text-cyan-400' : 'text-yellow-400'}`} />
          <span className={currentRoom ? 'text-cyan-400' : 'text-yellow-400'}>
            {currentRoom || 'Sélectionner pièce'}
          </span>
        </span>
      </button>
      
      {showDropdown && (
        <div 
          className="absolute top-full left-0 mt-1 w-full min-w-[140px] bg-black border border-cyan-400 rounded z-50 max-h-48 overflow-y-auto"
          style={{ boxShadow: '0 0 15px rgba(0, 240, 255, 0.4)' }}
        >
          {availableRooms.map((room) => (
            <button
              key={room}
              onClick={() => {
                selectRoom(room);
                setShowDropdown(false);
              }}
              className={`w-full text-left px-2 py-1.5 font-mono text-[10px] transition-colors ${
                room === currentRoom
                  ? 'bg-cyan-400 text-black font-bold'
                  : 'text-cyan-400 hover:bg-cyan-400/20'
              }`}
            >
              {room === currentRoom ? '▶ ' : '  '}{room}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default { useNetworkInfo, NetworkStatus, useRoomSelector, RoomSelector };
